import {
  arrow,
  autoPlacement,
  autoUpdate,
  computePosition,
  offset,
  shift,
} from '@floating-ui/dom';
import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { iconCircleInfo, iconX } from '@/components/icons';
import { GlobalComponentType, global } from '@/context';
import { ANIMATION_SPEED, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';

@customElement('fg-tooltip')
export class FGTooltip extends LitElement {
  @property({ type: String })
  tooltipTitle = '';

  @property({ type: String })
  tooltipData: string | null = null;

  @property({ type: Object })
  parentRef?: Ref<Element>;

  @query('.arrow')
  arrowElement?: HTMLElement;

  @state()
  private show = true;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  private tooltipButtonRef = createRef<HTMLButtonElement>();
  private tooltipContentRef = createRef<HTMLDivElement>();

  private toggleTooltip(event: Event, show: boolean) {
    if (
      (window.innerWidth > BREAKPOINT_SMALL_PX && event.type === 'click') ||
      (window.innerWidth < BREAKPOINT_SMALL_PX &&
        event.type === 'mouseenter') ||
      (window.innerWidth < BREAKPOINT_SMALL_PX && event.type === 'mouseleave')
    ) {
      return;
    }

    this.show = show;
  }

  positionTooltip = () => {
    const boundary = this.parentRef?.value ?? document;
    if (!this.tooltipButtonRef.value || !this.tooltipContentRef?.value) return;
    computePosition(
      this.tooltipButtonRef?.value,
      this.tooltipContentRef?.value,
      {
        strategy: 'fixed',
        middleware: [
          offset(10),
          autoPlacement(),
          shift({
            altBoundary: true,
            ...(boundary && { boundary: boundary as Element }),
          }),
          this.arrowElement && arrow({ element: this.arrowElement }),
        ],
      },
    ).then(({ x, y, middlewareData, placement }) => {
      if (middlewareData.arrow && this.arrowElement) {
        const side = placement.split('-')[0];
        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[side];
        if (!staticSide) return;
        const { x: xArrow, y: yArrow } = middlewareData.arrow;
        Object.assign(this.arrowElement.style, {
          left: xArrow != null ? `${xArrow}px` : '',
          top: yArrow != null ? `${yArrow}px` : '',
          [staticSide]: `${-this.arrowElement.offsetWidth / 2}px`,
          transform: 'rotate(45deg)',
        });
      }

      if (!this.tooltipContentRef?.value) return;
      Object.assign(this.tooltipContentRef?.value.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  render() {
    let title;

    if (this.tooltipTitle) {
      title = html`
        <h4>${this.tooltipTitle}</h4>
      `;
    }

    if (this.tooltipData) {
      const { layout } = this.globalContext;

      return html`
        <span class="tooltip-wrapper">
          <button
            class=${classMap({
              'tool-tip-fill-retail': layout === 'retail',
              'tool-tip-fill-wholesale': layout === 'wholesale',
              tooltip: true,
            })}
            ${ref(this.tooltipButtonRef)}
            @mouseenter=${(event: Event) => this.toggleTooltip(event, false)}
            @mouseleave=${(event: Event) => this.toggleTooltip(event, true)}
            @click=${(event: Event) => this.toggleTooltip(event, !this.show)}
          >
            ${iconCircleInfo}
          </button>
          <div
            class="tooltip-content"
            ${ref(this.tooltipContentRef)}
            aria-hidden=${this.show}
          >
            <div class="arrow"></div>
            <span
              class="tooltip-close"
              @click=${(event: Event) => this.toggleTooltip(event, true)}
            >
              ${iconX}
            </span>
            ${title || nothing}
            <p>${this.tooltipData}</p>
          </div>
        </span>
      `;
    }
  }

  firstUpdated() {
    super.connectedCallback();
    if (!this.tooltipButtonRef.value || !this.tooltipContentRef?.value) return;
    Object.assign(this.tooltipContentRef?.value.style, {
      position: 'fixed',
    });
    autoUpdate(
      this.tooltipButtonRef?.value,
      this.tooltipContentRef?.value,
      this.positionTooltip,
    );
  }

  static styles = [
    baseStyles,
    css`
      .tooltip-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .tooltip-wrapper,
      .tooltip {
        width: 16px;
        height: 16px;
      }

      .tool-tip-fill-retail {
        fill: var(--fg-primary, var(--fg-fallback-primary));
      }

      .tool-tip-fill-wholesale {
        fill: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      .tooltip svg {
        transition: fill ease-in-out ${ANIMATION_SPEED}s;
      }

      .tool-tip-fill-retail:hover svg {
        fill: var(--fg-primary--dark, var(--fg-fallback-primary--dark));
      }

      .tool-tip-fill-wholesale:hover svg {
        fill: var(--fg-secondary--dark, var(--fg-fallback-secondary--dark));
      }

      .tooltip-content {
        display: block;
        background-color: #ffffff;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        border: 1px solid var();
        max-width: 320px;
        padding: 16px;
        z-index: 10;
      }

      .arrow {
        position: absolute;
        width: 8px;
        height: 8px;
        background-color: white;
      }

      .tooltip-content[aria-hidden='true'] {
        visibility: hidden;
      }

      .tooltip-close {
        position: absolute;
        right: 5px;
        top: 5px;
      }

      .tooltip-close svg {
        width: 16px;
        height: 16px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .tooltip-content svg {
          display: none;
        }
      }
    `,
  ];
}
